import { addPipelineSchema } from '../../../schema/pipeline_schema';
import { createPipelineThunk } from '../../../redux/thunks/pipeline';
import CustomDropdown from '../../reusable/dropdown';
import CustomInput from '../../reusable/input';
import CustomModal from '../../modals';
import LabelWrapper from '../../wrapper/label';
import { PipelineParams } from '../../../params/pipeline';
import { useParams } from 'react-router';
import { AppDispatch, RootState } from '../../../redux/store';
import { Box, Button, Typography } from '@mui/material';
import { Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';

/**
 * @heading1: AddPipelineModal
 * @paragraph: A modal component for adding a new pipeline. Uses Formik for form handling
 * and integrates with Redux for pipeline creation.
 * @param: {boolean} open - Whether the modal is open or not.
 * @param: {function} onClose - Function to close the modal.
 */

const AddPipelineModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
    /**
     * @heading3: Variables
     * @ol:
     * initialValues: {PipelineParams} -  Initial form values for the pipeline creation form.
     * dispatch: {AppDispatch} - The Redux dispatch function to trigger actions.
     * loaders: An object containing the loader details from the Redux store.
     */
    // Initial form values for the pipeline creation form
    const initialValues: PipelineParams = {
        name: '',
        is_default: 0,
        deal_default_value: 0,
        client_id: 0,
        template_id: 0
    };
    const { id } = useParams();
    const dispatch = useDispatch<AppDispatch>();
    const { loaders, templates } = useSelector((state: RootState) => state.pipeline);

    /**
     * @heading3: Functions
     * @heading4: Handles the form submission by dispatching a Redux thunk.
     * @param: {PipelineParams} values - The form values for creating a pipeline.
     */
    const handleSubmit = async (values: PipelineParams) => {
        try {
            // Dispatch the createPipelineThunk with form values
            await dispatch(createPipelineThunk({
                name: values.name,
                is_default: values.is_default,
                deal_default_value: values.deal_default_value,
                client_id: id ? parseInt(id as string, 10) : 0,
                template_id: values.template_id
            })).unwrap();
            // Close the modal on successful submission
            onClose();
        } catch (e) {
            // Catch errors
        }
    };

    return <CustomModal open={open} handleClose={loaders.isCreating ? () => null : onClose} title="Adding New pipeline">
        <Formik
            initialValues={initialValues}
            validationSchema={addPipelineSchema}
            onSubmit={handleSubmit}
        >
            {({ values, handleChange, handleBlur, errors, touched, setFieldValue }) => (
                <Form>
                    <Box display={'flex'} flexDirection="column" gap={2}>
                        {/* Input field for pipeline name */}
                        <CustomInput
                            value={values.name}
                            onChange={handleChange('name')}
                            onBlur={handleBlur('name')}
                            error={touched.name && errors.name}
                            fullWidth
                            label="Pipeline Name"
                            placeholder="Pipeline Name"
                            variant="outlined"
                            disabled={loaders.isCreating}
                            mandatoryFields={true}
                        />
                        {/* Input field for default deal value */}
                        <CustomInput
                            type="number"
                            value={values.deal_default_value}
                            onChange={handleChange('deal_default_value')}
                            onBlur={handleBlur('deal_default_value')}
                            error={touched.deal_default_value && errors.deal_default_value}
                            fullWidth
                            label={
                                <>
                                    Deal Default Value <Typography component="span" fontSize="0.85rem" color="text.secondary">(dollar amount per deal)</Typography>
                                </>
                            }
                            // label="Deal Default Value (dollar amount per deal)"
                            placeholder="Enter value"
                            variant="outlined"
                            disabled={loaders.isCreating}
                        />
                        {!id && <Box>
                            <LabelWrapper label="Choose a Stage Template" />
                            <CustomDropdown
                                value={values.template_id}
                                onChange={(e) => setFieldValue('template_id', e.target.value === 'choose' ? 0 : e.target.value)}
                                enabledDefaultSelection={true}
                                placeholder="Select Template"
                                options={templates.map((template) => ({
                                    label: template.template_name,
                                    value: template.id
                                }))}
                            />
                        </Box>}
                        {/* Checkbox to set the pipeline as default */}
                        <CustomInput
                            type="checkbox"
                            label="Make this default pipeline"
                            checked={values.is_default === 1}
                            onChange={(e) => setFieldValue('is_default', e.target.checked ? 1 : 0)}
                            disabled={loaders.isCreating}
                        />
                        {/* Action buttons for form submission or cancellation */}
                        <Box display="flex" gap={2}>
                            <Button variant="outlined" color="primary" onClick={onClose} fullWidth disabled={loaders.isCreating}>
                                Cancel
                            </Button>
                            <Button type="submit" variant="contained" color="primary" fullWidth disabled={loaders.isCreating}>
                                {loaders.isCreating ? 'Creating...' : 'Create'}
                            </Button>
                        </Box>
                    </Box>
                </Form>
            )}
        </Formik>
    </CustomModal>;
};

export default AddPipelineModal;